import React from 'react';
import Header from '../../components/Header';
import { Shield, Users, AlertTriangle, CheckCircle, ArrowLeft, Gavel, Scale, UserCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Seo } from '../../src/seo/Seo';

const RightsResponsibilities: React.FC = () => {
  // Fonctions par défaut pour les props du Header
  const handleDownloadClick = () => {
    // Rediriger vers la page d'accueil pour le téléchargement
    window.location.href = '/';
  };

  const handleMySpaceClick = () => {
    // Rediriger vers la page d'accueil pour My Space
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50">
      <Seo
        title={{ fr: 'CGU — Droits & responsabilités — Burok', en: 'Terms — Rights & responsibilities — Burok' }}
        description={{
          fr: "Détails sur vos droits, responsabilités et le cadre juridique lié à l'utilisation de Burok.",
          en: 'Details about your rights, responsibilities, and the legal framework for using Burok.',
        }}
        canonicalPath={{ fr: '/cgu/rights-responsibilities', en: '/en/cgu/rights-responsibilities' }}
      />
      <Header onDownloadClick={handleDownloadClick} onMySpaceClick={handleMySpaceClick} />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <Scale className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Droits et Responsabilités des Utilisateurs
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Guide complet de vos droits et obligations lors de l'utilisation de Burok
          </p>
          <Link
            to="/cgu"
            className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-medium rounded-full hover:bg-gray-700 transition-all duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux CGU
          </Link>
        </div>
      </section>

      {/* Content Section */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            
            {/* User Rights */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <UserCheck className="w-6 h-6 text-green-500 mr-3" />
                Vos Droits en Tant qu'Utilisateur
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                  <h3 className="text-xl font-semibold text-green-900 mb-4">Droits Fondamentaux</h3>
                  <ul className="space-y-3 text-green-800">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Accès complet à toutes les fonctionnalités de l'application</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Support technique gratuit et assistance clientèle</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Mises à jour régulières et nouvelles fonctionnalités</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Protection de vos données personnelles</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Résiliation de votre compte à tout moment</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">Droits RGPD</h3>
                  <ul className="space-y-3 text-blue-800">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Droit d'accès à toutes vos données personnelles</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Droit de rectification des informations inexactes</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Droit à l'effacement (droit à l'oubli)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Droit à la portabilité de vos données</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span>Droit de limitation du traitement</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* User Responsibilities */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Users className="w-6 h-6 text-indigo-500 mr-3" />
                Vos Responsabilités et Obligations
              </h2>
              
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-yellow-900 mb-4 flex items-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3" />
                    Obligations Légales et Réglementaires
                  </h3>
                  <p className="text-yellow-800 mb-4">
                    En utilisant Burok, vous vous engagez à respecter toutes les lois et réglementations applicables 
                    dans votre juridiction, notamment en matière de finances personnelles et de protection des données.
                  </p>
                  <ul className="text-yellow-800 space-y-2 text-sm">
                    <li>• Respect des lois fiscales et comptables de votre pays</li>
                    <li>• Conformité aux réglementations sur la protection des données</li>
                    <li>• Respect des lois anti-blanchiment d'argent si applicable</li>
                    <li>• Conformité aux réglementations financières locales</li>
                    <li>• Respect des droits de propriété intellectuelle</li>
                  </ul>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-orange-900 mb-4 flex items-center">
                    <Shield className="w-5 h-5 text-orange-600 mr-3" />
                    Sécurité et Protection du Compte
                  </h3>
                  <p className="text-orange-800 mb-4">
                    Vous êtes entièrement responsable de la sécurité de votre compte et de la protection de vos 
                    informations d'identification.
                  </p>
                  <ul className="text-orange-800 space-y-2 text-sm">
                    <li>• Utilisation de mots de passe forts et uniques</li>
                    <li>• Activation de l'authentification à deux facteurs</li>
                    <li>• Protection de vos appareils contre les logiciels malveillants</li>
                    <li>• Déconnexion après chaque session sur des appareils partagés</li>
                    <li>• Signalement immédiat de toute activité suspecte</li>
                  </ul>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-red-900 mb-4 flex items-center">
                    <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
                    Utilisation Appropriée du Service
                  </h3>
                  <p className="text-red-800 mb-4">
                    Vous vous engagez à utiliser Burok uniquement pour des fins légitimes et personnelles, 
                    en respectant les droits d'autrui et l'intégrité du service.
                  </p>
                  <ul className="text-red-800 space-y-2 text-sm">
                    <li>• Interdiction d'utiliser le service à des fins illégales</li>
                    <li>• Respect de la vie privée des autres utilisateurs</li>
                    <li>• Prohibition du harcèlement ou de l'intimidation</li>
                    <li>• Interdiction de diffuser du contenu offensant ou inapproprié</li>
                    <li>• Respect des droits de propriété intellectuelle</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Legal Framework */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Gavel className="w-6 h-6 text-purple-500 mr-3" />
                Cadre Juridique et Réglementaire
              </h2>
              
              <div className="space-y-6">
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-purple-900 mb-4">Législation Applicable</h3>
                  <p className="text-purple-800 mb-4">
                    Les présentes CGU sont régies par le droit français et les réglementations européennes applicables. 
                    En cas de litige, les tribunaux français seront seuls compétents.
                  </p>
                  <ul className="text-purple-800 space-y-2">
                    <li>• Droit français pour l'interprétation et l'exécution</li>
                    <li>• Règlement Général sur la Protection des Données (RGPD)</li>
                    <li>• Directive ePrivacy et réglementations nationales</li>
                    <li>• Code de la consommation et droit des contrats</li>
                    <li>• Réglementations financières et bancaires applicables</li>
                  </ul>
                </div>
                
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-indigo-900 mb-4">Résolution des Litiges</h3>
                  <p className="text-indigo-800 mb-4">
                    En cas de désaccord ou de litige, nous nous engageons à rechercher une solution amiable 
                    avant toute procédure judiciaire.
                  </p>
                  <ul className="text-indigo-800 space-y-2">
                    <li>• Contact direct avec notre service clientèle</li>
                    <li>• Médiation par un tiers indépendant si nécessaire</li>
                    <li>• Recours aux tribunaux de commerce français</li>
                    <li>• Possibilité d'arbitrage pour les litiges commerciaux</li>
                    <li>• Application de la loi française en dernier recours</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
                Limitation de Responsabilité Détaillée
              </h2>
              
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <p className="text-red-800 mb-6 font-medium">
                  Burok s'efforce de fournir un service de qualité mais ne peut garantir une disponibilité 
                  continue ou l'absence d'erreurs. Voici les limites de notre responsabilité :
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-red-900 mb-3">Exclusions de Responsabilité</h4>
                    <ul className="text-red-800 space-y-2 text-sm">
                      <li>• Pertes financières dues à des erreurs de saisie utilisateur</li>
                      <li>• Dommages indirects ou consécutifs</li>
                      <li>• Perte de profits ou d'opportunités commerciales</li>
                      <li>• Dommages causés par des tiers ou des événements extérieurs</li>
                      <li>• Conséquences de la non-conformité aux obligations légales</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-red-900 mb-3">Limites Financières</h4>
                    <ul className="text-red-800 space-y-2 text-sm">
                      <li>• Responsabilité limitée au montant des frais payés</li>
                      <li>• Plafond maximum de 100€ par incident</li>
                      <li>• Exclusion des dommages corporels</li>
                      <li>• Limitation aux dommages directs prouvés</li>
                      <li>• Application des clauses de force majeure</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Questions sur Vos Droits ?
              </h2>
              <p className="text-gray-600 mb-6">
                Si vous avez des questions sur vos droits ou responsabilités, ou si vous souhaitez 
                exercer vos droits RGPD, notre équipe est là pour vous accompagner.
              </p>
              <div className="space-x-4">
                <a
                  href="mailto:privacy@burok.app"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-full hover:shadow-lg hover:scale-105 transition-all duration-200"
                >
                  Contacter le DPO
                </a>
                <Link
                  to="/cgu"
                  className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-medium rounded-full hover:bg-gray-700 transition-all duration-200"
                >
                  Retour aux CGU
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default RightsResponsibilities;
